import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import {
  Form, PrimaryButton, FieldTextInput, FieldSelect, FieldPhoneNumberInput, CustomExtendedDataField
} from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'SignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.emailPlaceholder',
                })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.firstNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.middleNameRoot}
                  type="text"
                  id={formId ? `${formId}.mname` : 'mname'}
                  name="mname"
                  autoComplete="additional-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.middleNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.middleNamePlaceholder',
                  })}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.lastNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <FieldTextInput
                className={css.password}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />

              <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <FieldTextInput
                className={css.privateField}
                type="date"
                id={formId ? `${formId}.dateOfBirth` : 'dateOfBirth'}
                name="dateOfBirth"
                autoComplete="bday"
                label={intl.formatMessage({
                  id: 'SignupForm.dateOfBirthLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.dateOfBirthPlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.dateOfBirthRequired',
                  })
                )}
              />

              <FieldSelect
                className={css.privateField}
                id={formId ? `${formId}.gender` : 'gender'}
                name="gender"
                label={intl.formatMessage({ id: 'SignupForm.genderTitle' })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.genderRequired',
                  })
                )}
              >
                <option disabled value="">
                  {intl.formatMessage({ id: 'SignupForm.genderPlaceholder' })}
                </option>
                <option value="male" key="male">
                  {intl.formatMessage({ id: 'SignupForm.genderMaleOption' })}
                </option>
                <option value="female" key="female">
                  {intl.formatMessage({ id: 'SignupForm.genderFemaleOption' })}
                </option>
                <option value="unknown" key="unknown">
                  {intl.formatMessage({ id: 'SignupForm.genderUnknownOption' })}
                </option>
              </FieldSelect>

              <FieldPhoneNumberInput
                className={css.privateField}
                id={formId ? `${formId}.mobilePhoneNumber` : 'mobilePhoneNumber'}
                name="mobilePhoneNumber"
                label={intl.formatMessage({
                  id: "SignupForm.mobilePhoneNumberLabel",
                })}
                placeholder={intl.formatMessage({
                  id: "SignupForm.mobilePhoneNumberPlaceholder",
                })}
              />

              <FieldTextInput
                className={css.privateField}
                type="text"
                id={formId ? `${formId}.streetName` : 'streetName'}
                name="streetName"
                autoComplete="address-line1"
                label={intl.formatMessage({
                  id: 'SignupForm.streetNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.streetNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.streetNameRequired',
                  })
                )}
              />

              <FieldTextInput
                className={css.privateField}
                type="text"
                id={formId ? `${formId}.houseNumber` : 'houseNumber'}
                name="houseNumber"
                autoComplete="address-line2"
                label={intl.formatMessage({
                  id: 'SignupForm.houseNumberLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.houseNumberPlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.houseNumberRequired',
                  })
                )}
              />

              <FieldTextInput
                className={css.privateField}
                type="text"
                id={formId ? `${formId}.postalCode` : 'postalCode'}
                name="postalCode"
                autoComplete="postal-code"
                label={intl.formatMessage({
                  id: 'SignupForm.postalCodeLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.postalCodePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.postalCodeRequired',
                  })
                )}
              />

              <FieldTextInput
                className={css.privateField}
                type="text"
                id={formId ? `${formId}.city` : 'city'}
                name="city"
                autoComplete="address-level2"
                label={intl.formatMessage({
                  id: 'SignupForm.cityLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.cityPlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.cityRequired',
                  })
                )}
              />
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
